import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getLeads } from '../redux/leadsSlice';
// import { clearMemberDetails } from '../redux/memberSlice'; // Removed

import Header from '../components/Header';
import Footer from '../components/Footer';
import { submitMemberRegistration, getInvestorRegistrationDetails, getMemberRegistrationDetails } from '../services/api';

// Sub-components
import { PREDEFINED_CITIES, PREDEFINED_STATES } from '../utils/constants';

import BasicDetails from './becomeMember/BasicDetails';
import BankDetails from './becomeMember/BankDetails';
import ProfessionalDetails from './becomeMember/ProfessionalDetails';
import AssetClasses from './becomeMember/AssetClasses';
import InvestmentAndDeclaration from './becomeMember/InvestmentAndDeclaration';
import DocumentUpload from './becomeMember/DocumentUpload';

import Loader from '../components/ui/loader/Loader';

const BecomeMember = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [apiMessage, setApiMessage] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showLoader, setShowLoader] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has('inv_reg_id') || params.has('INV_REG_ID') || params.has('MEM_REG_ID');
  });
  const [loaderMessage, setLoaderMessage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return (params.has('inv_reg_id') || params.has('INV_REG_ID') || params.has('MEM_REG_ID'))
      ? 'Loading user details, please wait...'
      : 'We are processing your registration, please wait...';
  });
  const [registeredMemberId, setRegisteredMemberId] = useState('');
  const [isAlreadyMember, setIsAlreadyMember] = useState(false);
  // const [isEmailDisabled, setIsEmailDisabled] = useState(false); // Removed state

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: leads = [], status } = useSelector((state) => state.leads);
  // Removed registrationDetails selector

  useEffect(() => {
    if (status === 'idle') {
      dispatch(getLeads({ page: 1, type: "all" }));
    }
  }, [dispatch, status]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const dealParam = searchParams.get('deal'); // No replacement, keep it raw
    const invRegId = searchParams.get('INV_REG_ID');
    const memRegId = searchParams.get('MEM_REG_ID');

    if (invRegId) {
      // If INV_REG_ID exists, fetch as investor
      fetchMemberData(invRegId, dealParam, 'investor');
    } else if (memRegId) {
      // If MEM_REG_ID exists, fetch as member
      fetchMemberData(memRegId, dealParam, 'member');
    } else {
      // If no member ID is present, we should redirect to SignUp page
      // Preserve deal param if present
      let target = '/invest-now';
      if (dealParam) {
        target += `?deal=${encodeURIComponent(dealParam)}`;
      }
      navigate(target);
    }

    // Determine which deal to select. Priority: URL Param (fetching logic inside fetchMemberData handles the rest)
    let dealName = dealParam;

    // console.log(selectedDeal);
    if (dealName && leads.length > 0) {
      // Find exact match by name (ID)
      const match = leads.find(d => d.name === dealName);
      if (match) {
        setSelectedDeal(match);
      }
    }
  }, [location.search, leads]); // Removed registrationDetails dependency

  // Scroll to top whenever step changes
  useEffect(() => {
    const element = document.getElementById('member_container');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentStep]);

  // Form state for Step 1: Basic Information & Investment Preferences
  const [formData, setFormData] = useState({
    // Basic Information
    inv_reg_id: '',
    mem_reg_id: '',
    deals: '',
    full_name: '',
    mobilecountrycode: '+91',
    mobile_phone: '',
    gender: '',
    email: '',
    dob: '',
    aadhar_number: '',

    address1: '',
    address2: '',
    state: '',
    city: '',
    pincode: '',
    pan_number: '',

    // Bank Details
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    branch_name: '',

    // Occupation / Business
    business_type: '',
    // Industry
    industry: [],

    // Designation
    designation: '',
    other_designation: '',

    // Coordinator Contact
    coordinator_name: '',
    coordinator_contact_no: '',

    // Nominee Details
    nominee_name: '',
    nominee_relation: '',

    // Other Info
    other_info: '',

    // Asset Classes for Investment Options
    real_estate: [],
    realEstateOther: '', // For custom input when "other" is selected
    running_business: [],
    distresses_assets: [],
    other_asset_types: [],

    // Step 2: Risk Profile, Declaration & Payment
    // Investment History, Horizon & Risk Appetite & preferences
    investing_experience: '',
    time_horizon: '',
    risk_appetite: '',
    preferred_locations: [], // Array for multi-input

    // Scale
    scale: '',

    // Open for any Joint Investment
    open_for_any_joint_investment: '',

    // Declaration checkboxes (3 points)
    declaration_1: false,
    declaration_2: false,
    declaration_3: false,
    name_signature: '',
    sign: '',
    date: '',
    place: '',

    // Fee Payment Details
    instrument_no__rtgs_ref_no: '',
    fee_date: '',
    amount_in_words: '',
    documentsAttached: {
      pan: false,
      aadhaar: false,
      bankDetails: false,
      passportCopy: false,
      drivingLicense: false,
      coiMoaAoa: false,
      boardResolution: false
    },
    uploadedFiles: {},

    // For CBP use only
    approved_by: '',
    approved_date: '',
    membership_number: '',
    approver_sign: '',

    // Terms & Conditions
    check_ztpjp: false
  });

  const fetchMemberData = async (memberId, urlDeal, type = 'investor') => {
    setLoaderMessage('Loading user details, please wait...');
    setShowLoader(true);
    try {
      let data;
      if (type === 'investor') {
        data = await getInvestorRegistrationDetails(memberId);
      } else {
        data = await getMemberRegistrationDetails(memberId);
      }
      // console.log(data);
      if (data) {
        if (data.isMember === 1) {
          setIsAlreadyMember(true);
          setShowLoader(false);
          return;
        }
        // Prioritize URL deal if present, otherwise fall back to API deal
        const effectiveDeal = urlDeal || data.intrested_in_deals || data.deals || '';

        const userData = data.user || {};

        setFormData(prev => ({
          ...prev,
          inv_reg_id: type === 'investor' ? memberId : prev.inv_reg_id,
          mem_reg_id: type === 'member' ? memberId : prev.mem_reg_id,
          full_name: userData.full_name || prev.full_name,
          email: userData.email || prev.email,
          mobile_phone: userData.mobile_phone || prev.mobile_phone,

          address1: userData.address1 || prev.address1,
          address2: userData.address2 || prev.address2,
          state: userData.state || prev.state,
          city: userData.city || prev.city,
          pincode: userData.pincode || prev.pincode,
          mobilecountrycode: userData.mobilecountrycode || userData.mobile_country_code || prev.mobilecountrycode,
          deals: effectiveDeal || prev.deals
        }));

        // Use effectiveDeal to set selected deal
        if (effectiveDeal && leads.length > 0) {
          const match = leads.find(d => d.name === effectiveDeal);
          if (match) setSelectedDeal(match);
        }

        setCurrentStep(1);
      }
    } catch (error) {
      console.error("Failed to fetch member data", error);
    } finally {
      setShowLoader(false);
    }
  };

  // Update formData when selectedDeal changes
  useEffect(() => {
    if (selectedDeal) {
      setFormData(prev => ({
        ...prev,
        deals: selectedDeal.name
      }));
    }
  }, [selectedDeal]);

  const [errors, setErrors] = useState({});
  const [validatedFields, setValidatedFields] = useState({}); // Track validated fields for green checkmark
  const [locationInput, setLocationInput] = useState(''); // Temp input for preferred locations
  const [industryInput, setIndustryInput] = useState(''); // Temp input for industry

  // Handle adding preferred location
  const handleAddLocation = (e) => {
    if (e.key === 'Enter' && locationInput.trim()) {
      e.preventDefault();
      setFormData(prev => ({
        ...prev,
        preferred_locations: [...prev.preferred_locations, locationInput.trim()]
      }));
      setLocationInput('');
    }
  };

  // Handle removing preferred location
  const handleRemoveLocation = (index) => {
    setFormData(prev => ({
      ...prev,
      preferred_locations: prev.preferred_locations.filter((_, i) => i !== index)
    }));
  };

  // Generic helper to add industry
  const addIndustry = (val) => {
    if (val && val.trim()) {
      setFormData(prev => ({
        ...prev,
        industry: [...(Array.isArray(prev.industry) ? prev.industry : []), val.trim()]
      }));
      setIndustryInput('');
    }
  };

  // Handle adding industry
  const handleAddIndustry = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addIndustry(industryInput);
    }
  };

  // Handle adding industry on Blur
  const handleAddIndustryBlur = () => {
    addIndustry(industryInput);
  };

  // City Suggestion State
  const [filteredCities, setFilteredCities] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const cityWrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (cityWrapperRef.current && !cityWrapperRef.current.contains(event.target)) {
        setShowCitySuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [cityWrapperRef]);

  const handleCityKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setShowCitySuggestions(false);
    }
  };

  const handleCityChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, city: value }));

    if (value.trim()) {
      const filtered = PREDEFINED_CITIES.filter(city =>
        city.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCities(filtered);
      setShowCitySuggestions(true);
    } else {
      setShowCitySuggestions(false);
    }

    if (errors.city) {
      setErrors(prev => ({ ...prev, city: '' }));
    }
  };

  const handleSelectCity = (city) => {
    setFormData(prev => ({ ...prev, city: city }));
    setShowCitySuggestions(false);
    if (errors.city) {
      setErrors(prev => ({ ...prev, city: '' }));
    }
  };

  // Handle removing industry
  const handleRemoveIndustry = (index) => {
    setFormData(prev => ({
      ...prev,
      industry: (Array.isArray(prev.industry) ? prev.industry : []).filter((_, i) => i !== index)
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle documentsAttached checkboxes
    if (name.startsWith('documentsAttached.')) {
      const docType = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        documentsAttached: {
          ...prev.documentsAttached,
          [docType]: checked
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Format mobile number (10 digits only)
  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    if (value.length <= 10) {
      setFormData(prev => ({ ...prev, mobile_phone: value }));
      if (errors.mobile_phone) {
        setErrors(prev => ({ ...prev, mobile_phone: '' }));
      }
    }
  };

  // Format Aadhaar with auto-dash (XXXX-XXXX-XXXX)
  const handleAadhaarChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove non-digits

    // Limit to 12 digits
    if (value.length > 12) {
      value = value.slice(0, 12);
    }

    // Add dashes for display
    let formatted = value;
    if (value.length > 4) {
      formatted = value.slice(0, 4) + '-' + value.slice(4);
    }
    if (value.length > 8) {
      formatted = value.slice(0, 4) + '-' + value.slice(4, 8) + '-' + value.slice(8);
    }

    setFormData(prev => ({ ...prev, aadhar_number: value })); // Store without dashes
    e.target.value = formatted; // Display with dashes

    if (errors.aadhar_number) {
      setErrors(prev => ({ ...prev, aadhar_number: '' }));
    }
  };

  // Format PAN (uppercase, alphanumeric)
  const handlePanChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (value.length <= 10) {
      setFormData(prev => ({ ...prev, pan_number: value }));
      if (errors.pan_number) {
        setErrors(prev => ({ ...prev, pan_number: '' }));
      }
    }
  };

  // Format DOB with auto-slash (DD-MM-YYYY)
  const handleDobChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove non-digits

    // Limit to 8 digits
    if (value.length > 8) {
      value = value.slice(0, 8);
    }

    // Add slashes for display
    let formatted = value;
    if (value.length > 2) {
      formatted = value.slice(0, 2) + '-' + value.slice(2);
    }
    if (value.length > 4) {
      formatted = value.slice(0, 2) + '-' + value.slice(2, 4) + '-' + value.slice(4);
    }

    setFormData(prev => ({ ...prev, dob: formatted }));

    if (errors.dob) {
      setErrors(prev => ({ ...prev, dob: '' }));
    }
  };



  // Format coordinator contact (10 digits only)
  const handleCoordinatorContactChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    if (value.length <= 10) {
      setFormData(prev => ({ ...prev, coordinator_contact_no: value }));
    }
  };

  // Generic date change handler (DD-MM-YYYY format)
  const handleDateChange = (fieldName) => (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove non-digits

    // Limit to 8 digits
    if (value.length > 8) {
      value = value.slice(0, 8);
    }

    // Add slashes for display
    let formatted = value;
    if (value.length > 2) {
      formatted = value.slice(0, 2) + '-' + value.slice(2);
    }
    if (value.length > 4) {
      formatted = value.slice(0, 2) + '-' + value.slice(2, 4) + '-' + value.slice(4);
    }

    setFormData(prev => ({ ...prev, [fieldName]: formatted }));

    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  // Validate Email on blur
  const validateEmailOnBlur = () => {
    if (formData.email.trim()) {
      if (!/^[a-zA-Z0-9._\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,6}$/.test(formData.email)) {
        setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
        setValidatedFields(prev => ({ ...prev, email: false }));
      } else {
        setErrors(prev => ({ ...prev, email: '' }));
        setValidatedFields(prev => ({ ...prev, email: true }));
      }
    }
  };

  // Validate Mobile on blur
  const validateMobileOnBlur = () => {
    if (formData.mobile_phone.trim()) {
      if (!/^\d{10}$/.test(formData.mobile_phone)) {
        setErrors(prev => ({ ...prev, mobile_phone: 'Mobile must be exactly 10 digits' }));
        setValidatedFields(prev => ({ ...prev, mobile_phone: false }));
      } else {
        setErrors(prev => ({ ...prev, mobile_phone: '' }));
        setValidatedFields(prev => ({ ...prev, mobile_phone: true }));
      }
    }
  };

  // Validate PAN on blur
  const validatePanOnBlur = () => {
    if (formData.pan_number.trim()) {
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan_number)) {
        setErrors(prev => ({ ...prev, pan_number: 'Please enter a valid PAN (e.g., ABCDE1234F)' }));
        setValidatedFields(prev => ({ ...prev, pan_number: false }));
      } else {
        setErrors(prev => ({ ...prev, pan_number: '' }));
        setValidatedFields(prev => ({ ...prev, pan_number: true }));
      }
    }
  };

  // Validate Aadhaar on blur
  const validateAadhaarOnBlur = () => {
    if (formData.aadhar_number.trim()) {
      if (!/^\d{12}$/.test(formData.aadhar_number)) {
        setErrors(prev => ({ ...prev, aadhar_number: 'Aadhar must be exactly 12 digits' }));
        setValidatedFields(prev => ({ ...prev, aadhar_number: false }));
      } else {
        setErrors(prev => ({ ...prev, aadhar_number: '' }));
        setValidatedFields(prev => ({ ...prev, aadhar_number: true }));
      }
    }
  };

  // Validate DOB on blur
  const validateDobOnBlur = () => {
    if (formData.dob.trim()) {
      // Check format DD-MM-YYYY
      if (!/^\d{2}-\d{2}-\d{4}$/.test(formData.dob)) {
        setErrors(prev => ({ ...prev, dob: 'Date must be in DD-MM-YYYY format' }));
        setValidatedFields(prev => ({ ...prev, dob: false }));
        return;
      }

      // Validate actual date
      const parts = formData.dob.split('-');
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);

      const date = new Date(year, month - 1, day);
      const isValidDate = date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day;

      if (!isValidDate || year < 1900 || date > new Date()) {
        setErrors(prev => ({ ...prev, dob: 'Please enter a valid date' }));
        setValidatedFields(prev => ({ ...prev, dob: false }));
      } else {
        setErrors(prev => ({ ...prev, dob: '' }));
        setValidatedFields(prev => ({ ...prev, dob: true }));
      }
    }
  };

  // Validate Coordinator Contact on blur (optional field)
  const validateCoordinatorContactOnBlur = () => {
    if (formData.coordinator_contact_no.trim()) {
      if (!/^\d{10}$/.test(formData.coordinator_contact_no)) {
        setErrors(prev => ({ ...prev, coordinator_contact_no: 'Contact must be exactly 10 digits' }));
        setValidatedFields(prev => ({ ...prev, coordinator_contact_no: false }));
      } else {
        setErrors(prev => ({ ...prev, coordinator_contact_no: '' }));
        setValidatedFields(prev => ({ ...prev, coordinator_contact_no: true }));
      }
    }
  };

  // Helper validation functions
  const validateDateString = (dateStr, fieldName) => {
    if (!dateStr) return `${fieldName} is required`;

    // Check format DD-MM-YYYY
    if (!/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      return 'Date must be in DD-MM-YYYY format';
    }

    const parts = dateStr.split('-');
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    const date = new Date(year, month - 1, day);
    const isValidDate = date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day;

    if (!isValidDate || year < 1900) {
      return 'Please enter a valid date';
    }

    // Future date check
    if (date > new Date()) {
      return 'Date cannot be in the future';
    }

    return '';
  };

  const validateMobileString = (mobileStr) => {
    if (!mobileStr) return 'Required';
    if (!/^\d{10}$/.test(mobileStr)) return 'Must be exactly 10 digits';
    return '';
  };

  const validateStep1 = () => {
    const newErrors = {};

    // Basic Information validation
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full Name is required';
    }

    // Mobile validation (Req + Pattern)
    if (!formData.mobile_phone.trim()) {
      newErrors.mobile_phone = 'Mobile is required';
    } else if (!/^\d{10}$/.test(formData.mobile_phone)) {
      newErrors.mobile_phone = 'Mobile must be exactly 10 digits';
    }

    // Email validation (Req + Pattern)
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[a-zA-Z0-9._\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,6}$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // DOB validation (Optional but validated if entered)
    if (formData.dob.trim()) {
      const dobError = validateDateString(formData.dob, 'Date of Birth');
      if (dobError) {
        newErrors.dob = dobError;
      }
    }

    // Aadhaar validation (Optional but validated if entered)
    if (formData.aadhar_number.trim()) {
      if (!/^\d{12}$/.test(formData.aadhar_number)) {
        newErrors.aadhar_number = 'Aadhaar must be exactly 12 digits';
      }
    }

    // PAN validation (Optional but validated if entered)
    if (formData.pan_number.trim()) {
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan_number)) {
        newErrors.pan_number = 'Please enter a valid PAN (e.g., ABCDE1234F)';
      }
    }

    // Coordinator contact validation
    if (formData.coordinator_contact_no) {
      const contactError = validateMobileString(formData.coordinator_contact_no);
      if (contactError && contactError !== 'Required') {
        newErrors.coordinator_contact_no = 'Contact number must be exactly 10 digits';
      }
    }

    // Pincode validation (Optional)
    if (formData.pincode && formData.pincode.trim()) {
      if (!/^\d{6}$/.test(formData.pincode)) {
        newErrors.pincode = 'Pincode must be 6 digits';
      }
    }

    // Bank Details validation (Conditional)
    if (formData.bank_name && formData.bank_name.trim().length > 0) {
      if (!formData.account_number.trim()) {
        newErrors.account_number = 'Account Number is required';
      }
      if (!formData.ifsc_code.trim()) {
        newErrors.ifsc_code = 'IFSC Code is required';
      }
      if (!formData.branch_name.trim()) {
        newErrors.branch_name = 'Branch Name is required';
      }
    }

    // Designation validation (Conditional)
    if (formData.designation === 'Other' && !formData.other_designation?.trim()) {
      newErrors.other_designation = 'Please enter your designation';
    }

    setErrors(newErrors);

    // Scroll to first error
    if (Object.keys(newErrors).length > 0) {
      scrollToError(Object.keys(newErrors)[0]);
    }

    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (!formData.declaration_1) {
      newErrors.declaration_1 = 'Please accept declaration 1';
    }
    if (!formData.declaration_2) {
      newErrors.declaration_2 = 'Please accept declaration 2';
    }
    if (!formData.declaration_3) {
      newErrors.declaration_3 = 'Please accept declaration 3';
    }

    if (!formData.check_ztpjp) {
      newErrors.check_ztpjp = 'Please accept the Terms & Conditions';
    }

    setErrors(newErrors);

    // Scroll to error
    if (Object.keys(newErrors).length > 0) {
      scrollToError(Object.keys(newErrors)[0]);
    }

    return Object.keys(newErrors).length === 0;
  };

  // Scroll to first error field
  const scrollToError = (fieldName) => {
    setTimeout(() => {
      const errorElement = document.querySelector(`[name="${fieldName}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
    }, 100);
  };

  const handleContinueToStep2 = (e) => {
    e.preventDefault();
    if (validateStep1()) {
      setLoaderMessage('We are processing your registration, please wait...');
      setShowLoader(true);
      setTimeout(() => {
        setCurrentStep(2);
        setShowLoader(false);
      }, 500);
    }
  };

  const handleBackToStep1 = (e) => {
    e.preventDefault();
    setCurrentStep(1);
  };



  const handleFileUpload = (docKey, file) => {
    if (file) {
      setFormData(prev => ({
        ...prev,
        uploadedFiles: {
          ...prev.uploadedFiles,
          [docKey]: file
        }
      }));
    }
  };

  const handleRemoveFile = (docKey) => {
    setFormData(prev => {
      const newFiles = { ...prev.uploadedFiles };
      delete newFiles[docKey];
      return {
        ...prev,
        uploadedFiles: newFiles
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep2()) {
      return;
    }

    setIsSubmitting(true);
    setLoaderMessage('We are processing your registration, please wait...');
    setShowLoader(true);
    setSubmitStatus('');
    setApiMessage('');

    try {
      // Create a copy of formData to transform arrays to strings for the API
      const submissionData = { ...formData };

      // Convert arrays to comma-separated strings
      if (Array.isArray(submissionData.industry)) {
        submissionData.industry = submissionData.industry.join(', ');
      }
      if (Array.isArray(submissionData.preferred_locations)) {
        submissionData.preferred_locations = submissionData.preferred_locations.join(', ');
      }
      if (Array.isArray(submissionData.real_estate)) {
        submissionData.real_estate = submissionData.real_estate.join(', ');
      }
      if (Array.isArray(submissionData.running_business)) {
        submissionData.running_business = submissionData.running_business.join(', ');
      }
      if (Array.isArray(submissionData.distresses_assets)) {
        submissionData.distresses_assets = submissionData.distresses_assets.join(', ');
      }
      if (Array.isArray(submissionData.other_asset_types)) {
        submissionData.other_asset_types = submissionData.other_asset_types.join(', ');
      }

      // Execute API call and minimum delay concurrently
      const minDelay = new Promise(resolve => setTimeout(resolve, 2000));
      const apiRequest = submitMemberRegistration(submissionData);

      const [_, response] = await Promise.all([minDelay, apiRequest]);
      const data = await response.json();
      if (data.message.success === true) {
        setSubmitStatus('success');
        // Extract membership ID from response (assuming it's in data.message.name)
        const membershipId = data.message.name || 'N/A';

        // Redux clear removed as per new flow

        // Construct navigation URL
        let targetUrl = `/thank-you?member=${membershipId}`;
        if (selectedDeal && selectedDeal.name) {
          targetUrl += `&deal=${selectedDeal.name.replace(/\s+/g, '-')}`;
        }

        // Navigate
        navigate(targetUrl, { state: { selectedDeal } });

        // setShowSuccessModal(true); // No longer needed for success
      } else {
        setSubmitStatus('error');
        setApiMessage(data.message.message || 'Something went wrong during registration.');
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      setApiMessage('Something went wrong during registration.');
      setShowSuccessModal(true);
    } finally {
      setIsSubmitting(false);
      setShowLoader(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setSubmitStatus('');
    setApiMessage('');
    setSelectedDeal(null);
    setFormData({
      deals: '',
      full_name: '',
      mobilecountrycode: '+91',
      mobile_phone: '',
      gender: '',
      email: '',
      dob: '',
      aadhar_number: '',
      address1: '',
      address2: '',
      state: '',
      city: '',
      pincode: '',
      pan_number: '',
      bank_name: '',
      account_number: '',
      ifsc_code: '',
      branch_name: '',
      business_type: '',
      industry: [],
      designation: '',
      coordinator_name: '',
      coordinator_contact_no: '',
      nominee_name: '',
      nominee_relation: '',
      other_info: '',
      real_estate: '',
      realEstateOther: '',
      running_business: '',
      distresses_assets: '',
      other_asset_types: '',
      investing_experience: '',
      time_horizon: '',
      risk_appetite: '',
      preferred_locations: [],
      scale: '',
      open_for_any_joint_investment: '',
      declaration_1: false,
      declaration_2: false,
      declaration_3: false,
      name_signature: '',
      sign: '',
      date: '',
      place: '',
      instrument_no__rtgs_ref_no: '',
      fee_date: '',
      amount_in_words: '',
      documentsAttached: {
        pan: false,
        aadhaar: false,
        bankDetails: false,
        passportCopy: false,
        drivingLicense: false,
        coiMoaAoa: false,
        boardResolution: false
      },
      uploadedFiles: {},
      approved_by: '',
      approved_date: '',
      membership_number: '',
      approver_sign: '',
      check_ztpjp: false
    });
    setCurrentStep(1);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRetry = () => {
    setShowSuccessModal(false);
  };

  return (
    <main>
      <section className='cbp_layout_header'>
        <div className='container-fluid grey-border-b-1'>
          <Header></Header>
        </div>
      </section>
      <section className='cbp_layout_member' id='member_container'>
        <div className='container'>

          {showLoader && <Loader message={loaderMessage} />}

          {showSuccessModal && (
            /* Success/Error Modal Overlay */
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999
              }}
            >
              <div
                className="bg-white p-4 rounded shadow-lg text-center"
                style={{ maxWidth: '400px', width: '90%' }}
              >
                <div className={`mb-3 ${submitStatus === 'success' ? 'text-success' : 'text-danger'}`}>
                  {submitStatus === 'success' ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" fill="currentColor" className="bi bi-check-circle-fill" viewBox="0 0 16 16">
                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" fill="currentColor" className="bi bi-exclamation-circle-fill" viewBox="0 0 16 16">
                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4zm.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" />
                    </svg>
                  )}
                </div>
                <h4 className="mb-3">{submitStatus === 'success' ? 'Success!' : 'Error!'}</h4>
                <p className="mb-4">{apiMessage}</p>
                <button
                  className={`btn ${submitStatus === 'success' ? 'btn-primary' : 'btn-danger'} px-4`}
                  onClick={submitStatus === 'success' ? handleCloseModal : handleRetry}
                >
                  {submitStatus === 'success' ? 'OK' : 'Retry'}
                </button>
              </div>
            </div>
          )}

          {selectedDeal && !isAlreadyMember && (
            <div className="row justify-content-center mb-1 mt-4">
              <div className="col-12 col-md-9 col-lg-9">
                <div className="card shadow-sm border-0 rounded-3 overflow-hidden" style={{ background: '#f8f9fa' }}>
                  <div className="row g-0 align-items-center">
                    <div className="col-md-3">
                      <img
                        src={selectedDeal.deal_image}
                        className="img-fluid rounded-start h-100 w-100 object-fit-cover"
                        alt={selectedDeal.deal_property_title}
                        style={{ maxHeight: '80px', objectFit: 'cover' }}
                      />
                    </div>
                    <div className="col-md-9">
                      <div className="card-body py-3">
                        <h5 className="card-title fw-bold mb-1">{selectedDeal.deal_property_title}</h5>
                        {/* <p className="card-text text-muted mb-0">Time Left: {selectedDeal.time_left_in_days} days</p> */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isAlreadyMember ? (
            <div className="row justify-content-center mb-5 mt-4">
              <div className="col-12 col-md-8 text-center">
                <div className="card shadow-sm border-0 p-4 p-md-5">
                  <div className="mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" fill="#198754" className="bi bi-check-circle-fill" viewBox="0 0 16 16">
                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                    </svg>
                  </div>
                  <h4 className="msg-heading mb-3">You are already a registered member!</h4>
                  <p className="text-muted mb-4">You have already completed your membership registration. Explore our latest exclusive deals.</p>
                  <div>
                    <button
                      onClick={() => navigate('/live-deals')}
                      className="btn btn-primary sin-button px-5 py-3 rounded-pill shadow-sm border-0"
                      style={{ background: 'linear-gradient(to bottom, #0082CB 0%, #004E7A 100%)' }}
                    >
                      Explore More Deals
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Stepper Header - Step 1 is now first step here */}
              <div className="row justify-content-center mb-md-3 mt-md-2">
                <div className="col-12 col-md-10 col-lg-10">
                  <div className="stepper-header-new">
                    <div className={`stepper-box ${currentStep === 1 ? 'active' : 'completed'}`}>
                      <div className="stepper-box-content">
                        <span className="stepper-box-number">1</span>
                        <span className="stepper-box-label">Basic Information & Investment Preferences</span>
                      </div>
                    </div>
                    <div className="stepper-arrow d-none d-md-block d-lg-block d-xl-block">→</div>
                    <div className={`stepper-box ${currentStep === 2 ? 'active' : 'inactive'}`}>
                      <div className="stepper-box-content">
                        <span className="stepper-box-number">2</span>
                        <span className="stepper-box-label">Risk Profile, Declaration & Payment</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Container */}
              <div className="row justify-content-center mb-md-5">
                <div className="col-12 col-md-10 col-lg-10">
                  <div className="membership-form-container">
                    {/* Global Success/Error Messages for Full Form (Step 1 & 2) */}
                    {currentStep > 0 && submitStatus === 'success' && (
                      <div className="col-12 alert alert-success mb-3">
                        {apiMessage}
                      </div>
                    )}
                    {currentStep > 0 && submitStatus === 'error' && (
                      <div className="col-12 alert alert-danger mb-3">
                        {apiMessage}
                      </div>
                    )}



                    {/* Step 1: Basic Information & Investment Preferences */}
                    {currentStep === 1 && (
                      <form onSubmit={handleContinueToStep2}>
                        <BasicDetails
                          formData={formData}
                          handleInputChange={handleInputChange}
                          handleDobChange={handleDobChange}
                          validateDobOnBlur={validateDobOnBlur}
                          handleMobileChange={handleMobileChange}
                          validateMobileOnBlur={validateMobileOnBlur}
                          validateEmailOnBlur={validateEmailOnBlur}
                          handleAadhaarChange={handleAadhaarChange}
                          validateAadhaarOnBlur={validateAadhaarOnBlur}
                          handlePanChange={handlePanChange}
                          validatePanOnBlur={validatePanOnBlur}
                          errors={errors}
                          validatedFields={validatedFields}
                          // City Auto-complete props
                          cityWrapperRef={cityWrapperRef}
                          filteredCities={filteredCities}
                          showCitySuggestions={showCitySuggestions}
                          handleCityChange={handleCityChange}
                          handleSelectCity={handleSelectCity}
                          handleCityKeyDown={handleCityKeyDown}
                          PREDEFINED_STATES={PREDEFINED_STATES}
                          PREDEFINED_CITIES={PREDEFINED_CITIES}
                        />

                        {/* <BankDetails
                          formData={formData}
                          handleInputChange={handleInputChange}
                          errors={errors}
                        /> */}

                        <ProfessionalDetails
                          formData={formData}
                          handleInputChange={handleInputChange}
                          handleCoordinatorContactChange={handleCoordinatorContactChange}
                          validateCoordinatorContactOnBlur={validateCoordinatorContactOnBlur}
                          industryInput={industryInput}
                          setIndustryInput={setIndustryInput}
                          handleAddIndustry={handleAddIndustry}
                          handleAddIndustryBlur={handleAddIndustryBlur}
                          handleRemoveIndustry={handleRemoveIndustry}
                          errors={errors}
                          validatedFields={validatedFields}
                        />

                        <AssetClasses
                          formData={formData}
                          handleInputChange={handleInputChange}
                          locationInput={locationInput}
                          setLocationInput={setLocationInput}
                          handleAddLocation={handleAddLocation}
                          handleRemoveLocation={handleRemoveLocation}
                        />

                        {/* Continue Button */}
                        <div className="form-actions">
                          <button type="submit" className="btn btn-primary btn-continue">
                            Continue to Step 2
                          </button>
                        </div>
                      </form>
                    )}

                    {/* Step 2: Risk Profile, Declaration & Payment */}
                    {currentStep === 2 && (
                      <form onSubmit={handleSubmit}>
                        {/* Back to Step 1 Link */}
                        <div className="back-to-step">
                          <button type="button" onClick={handleBackToStep1} className="back-link">
                            <span className="back-arrow">←</span> Back to Step 1
                          </button>
                        </div>

                        <InvestmentAndDeclaration
                          formData={formData}
                          handleInputChange={handleInputChange}
                          handleDateChange={handleDateChange}
                          errors={errors}
                        />

                        <DocumentUpload
                          formData={formData}
                          handleInputChange={handleInputChange}
                          handleFileUpload={handleFileUpload}
                          handleRemoveFile={handleRemoveFile}
                        />

                        {/* Terms & Conditions */}
                        <div className="form-section">


                          <div className="declaration-box checkbox-group-vertical">
                            <div className="checkbox-item d-flex align-items-start">
                              <input
                                type="checkbox"
                                id="check_ztpjp"
                                name="check_ztpjp"
                                checked={formData.check_ztpjp}
                                onChange={handleInputChange}
                                className={`form-checkbox ${errors.check_ztpjp ? 'error' : ''}`}
                              />
                              <label htmlFor="check_ztpjp" className="ms-2">
                                I have read and accept the <a href="/terms-conditions" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: 'blue' }}>Terms & Conditions</a>
                              </label>
                            </div>
                            {errors.check_ztpjp && <span className="error-message mb-3 ms-4 mt-0">{errors.check_ztpjp}</span>}
                          </div>
                        </div>

                        {/* Form Actions */}
                        <div className="form-actions step2-actions">
                          <button type="button" onClick={handleBackToStep1} className="btn btn-secondary btn-back">
                            Back to Previous
                          </button>
                          <button type="submit" className="btn btn-primary btn-submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Submitting...' : 'Submit Application'}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

        </div>
      </section>
      <section className='cbp_layout footer-section'>
        <div className='container'>
          <Footer></Footer>
        </div>
      </section>
    </main >
  );
};

export default BecomeMember;
